import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowUpRight } from "@/components/icons";
import { JobDetailContent } from "@/components/job-detail-content";
import { jobTitle } from "@/lib/format";
import type { JobDetail, JobSummary } from "@/lib/types";
import { getJob, searchJobs } from "@/src/api";
import { useAuth } from "@/src/auth-context";
import { useDocumentMeta } from "@/src/use-document-meta";

export function JobDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, openLogin } = useAuth();
  const initial = window.__CHUNO_JOB__?.job_id === id ? window.__CHUNO_JOB__ : null;
  const [job, setJob] = useState<JobDetail | null | undefined>(initial || undefined);
  const [related, setRelated] = useState<JobSummary[]>(initial ? window.__CHUNO_RELATED__ || [] : []);
  const title = job ? jobTitle(job) : "Job details";
  const description = job?.job_description?.slice(0, 155) ?? "";

  useDocumentMeta({
    title: job ? `${title} at ${job.company || "Employer"} | Chuno` : "Job details | Chuno",
    description,
    robots: job === null ? "noindex,nofollow" : "index,follow,max-image-preview:large",
    canonical: `https://www.chuno.work/jobs/${encodeURIComponent(id)}`,
  });

  useEffect(() => {
    if (window.__CHUNO_JOB__?.job_id === id) return;
    const controller = new AbortController();
    getJob(id, controller.signal).then((result) => {
      setJob(result);
      if (!result) return;
      const term = jobTitle(result).split(/\s+/).slice(0, 3).join(" ");
      return searchJobs({ query: term, limit: 4, signal: controller.signal }).then((response) => {
        setRelated(response.hits.filter((item) => item.job_id !== id).slice(0, 3));
      });
    }).catch(() => undefined);
    return () => controller.abort();
  }, [id]);

  if (job === undefined) return <main id="main" className="page-loading"><div><span />Loading role details…</div></main>;
  if (job === null) return <NotAvailable />;

  return <JobDetailContent job={job} related={related} authLoading={authLoading} onApply={() => {
    const destination = `/apply/${encodeURIComponent(id)}`;
    if (user) navigate(destination);
    else openLogin(destination);
  }} />;
}

function NotAvailable() {
  return (
    <main id="main" className="not-found-page">
      <div className="shell empty-state"><p className="eyebrow"><span /> Listing unavailable</p><h1>This role may have closed.</h1><p>Job markets move quickly. Explore current openings to find another strong match.</p><Link to="/jobs">Browse open roles <ArrowUpRight /></Link></div>
    </main>
  );
}
